import { getDemoSessionIfAllowed, isDemoAuthEnabled } from "../auth/authService";
import { getRoleStartPath, saveDemoSession } from "../demoAuth";
import type { Role } from "../../types";

interface AuthMeResponse {
  user?: {
    id: string;
    email: string;
    name?: string;
    role: Role;
  };
}

interface AuthLoginResponse extends AuthMeResponse {
  error?: string;
}

export function isFrontendDemoAuthEnabled(): boolean {
  const explicitFlag = import.meta.env.VITE_ENABLE_DEMO_AUTH;
  const localHost = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return localHost && isDemoAuthEnabled(explicitFlag);
}

export async function login(email: string, password: string): Promise<{ ok: boolean; message?: string; redirectTo?: string }> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return { ok: false, message: "Bitte geben Sie Ihre E-Mail-Adresse ein." };
  }
  if (!password.trim()) {
    return { ok: false, message: "Bitte geben Sie Ihr Passwort ein." };
  }

  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email: normalizedEmail, password })
  }).catch(() => undefined);

  if (response?.ok) {
    const data = (await response.json()) as AuthLoginResponse;
    if (!data.user) return { ok: false, message: "Login konnte nicht abgeschlossen werden." };
    saveDemoSession({
      userId: data.user.id,
      email: data.user.email,
      role: data.user.role,
      name: data.user.name ?? data.user.email
    });
    return { ok: true, redirectTo: getRoleStartPath(data.user.role) };
  }

  if (isFrontendDemoAuthEnabled()) {
    const demoSession = getDemoSessionIfAllowed(normalizedEmail, "true");
    if (demoSession) {
      saveDemoSession(demoSession);
      return { ok: true, redirectTo: getRoleStartPath(demoSession.role) };
    }
  }

  const errorData = response ? ((await response.json().catch(() => ({}))) as AuthLoginResponse) : undefined;
  return {
    ok: false,
    message: errorData?.error ?? "E-Mail oder Passwort ist nicht korrekt."
  };
}

export async function logoutProductionSession(): Promise<void> {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include"
  }).catch(() => undefined);
}

export async function getCurrentUser(): Promise<AuthMeResponse["user"] | undefined> {
  const response = await fetch("/api/auth/me", { credentials: "include" }).catch(() => undefined);
  if (!response?.ok) return undefined;
  const data = (await response.json()) as AuthMeResponse;
  return data.user;
}
